"use client";

import React, { useState, useMemo } from "react";
import { FileText, Search, ShieldCheck, BookOpen, ArrowRight, Layers, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InlineCta from "@/components/common/InlineCta";
interface TechDocItem {
  id: string;
  title: string;
  docCode: string;
  category: string;
  summary: string;
  pageCount: string;
  formatLabel: string;
  specsHighlight: string;
  link: string;
}
const FALLBACK_DOCUMENTS: TechDocItem[] = [{
  id: "doc-0",
  title: "Filtration Fundamentals: Mechanisms & Media Selection",
  docCode: "MF-FUND-001",
  category: "Engineering Data",
  summary: "Particle retention mechanisms, micron ratings, wire weave architectures, and hydraulic mechanics comparing surface versus depth filtration principles.",
  pageCount: "18 Pages",
  formatLabel: "Engineering Guide",
  specsHighlight: "Particle Retention & Media Mechanics",
  link: "/FiltrationFundamentalsPage"
}, {
  id: "doc-1",
  title: "ASTM E2016 Explained: Industrial Woven Wire Cloth Standard",
  docCode: "ASTM-E2016-20",
  category: "Compliance & Standards",
  summary: "Comprehensive guide to tolerance limits, wire diameter variations, aperture measurements, and warp/weft specifications per ASTM E2016.",
  pageCount: "12 Pages",
  formatLabel: "PDF Datasheet",
  specsHighlight: "Tolerances: +/-3% Wire Dia, ISO 9044 Compliant",
  link: "/AstmE2016ExplainedPage"
}, {
  id: "doc-2",
  title: "ISO Wire Mesh Standards & Technical Verification",
  docCode: "ISO-9044-2023",
  category: "Compliance & Standards",
  summary: "International benchmark definitions for industrial wire cloth, aperture calculation methods, open area percentage formulas, and quality control protocols.",
  pageCount: "16 Pages",
  formatLabel: "PDF Reference",
  specsHighlight: "ISO 9044 & ISO 4783 Specifications",
  link: "/ISOWireMeshStandardsPage"
}, {
  id: "doc-3",
  title: "How Wire Mesh Aperture & Open Area Are Measured",
  docCode: "MF-TECH-003",
  category: "Engineering Data",
  summary: "Mathematical equations and optical measurement methodologies for calculating mesh aperture size, wire diameter, and percent open area.",
  pageCount: "8 Pages",
  formatLabel: "Calculation Guide",
  specsHighlight: "Formula: Open Area % Calculation Model",
  link: "/WireMeshApertureMeasurementPage"
}, {
  id: "doc-4",
  title: "Stainless Steel Material Corrosion Resistance Matrix",
  docCode: "MF-MAT-316L",
  category: "Materials",
  summary: "Chemical compatibility matrix for 304, 304L, 316, 316L, 317L, Monel 400, and Hastelloy C-22 wire mesh across organic acids, chlorides, and alkaline solutions.",
  pageCount: "24 Pages",
  formatLabel: "Engineering Matrix",
  specsHighlight: "PREN Ratings & Pitting Resistance Data",
  link: "/CorrosionResistanceMatricesPage"
}, {
  id: "doc-5",
  title: "Sintered Multilayer Mesh Pressure Drop & Flow Velocity Chart",
  docCode: "MF-SINT-5L",
  category: "Product Reference",
  summary: "Flow rate versus differential pressure curves for standard 5-layer sintered stainless steel mesh in liquid and gas filtration systems.",
  pageCount: "10 Pages",
  formatLabel: "Technical Chart",
  specsHighlight: "Viscosity Delta P Calculations Included",
  link: "/SinteredMultilayerMeshPage"
}, {
  id: "doc-6",
  title: "Polymer Melt & Extrusion Filtration Quality SOP",
  docCode: "MF-SOP-EXTR",
  category: "Process & SOP",
  summary: "Standard operating procedure for continuous screen changer installation, pack burst pressure thresholds, thermal expansion allowances, and cleaning protocols.",
  pageCount: "14 Pages",
  formatLabel: "Operating Manual",
  specsHighlight: "High-Temp 400C & 350 Bar Rating Protocols",
  link: "/PolymerFiltrationPage"
}];
function TechnicalReferenceConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const displayDocs = useMemo<TechDocItem[]>(() => {
    if (posts && posts.length > 0) {
      return posts.map(post => ({
        id: post.id,
        title: post.title || "Untitled Technical Document",
        docCode: post.customFields?.doc_code || `MF-DOC-${post.id}`,
        category: post.customFields?.category || "Product Reference",
        summary: post.customFields?.summary || post.excerpt || "Detailed technical documentation and engineering parameters for industrial mesh applications.",
        pageCount: post.customFields?.page_count ? `${post.customFields.page_count} Pages` : "PDF Document",
        formatLabel: post.customFields?.format_label || "Technical Datasheet",
        specsHighlight: post.customFields?.specs_highlight || "Verified B2B Industrial Standard",
        link: post.link || "/TechnicalResourcesPage"
      }));
    }
    return FALLBACK_DOCUMENTS;
  }, [posts]);
  const categories = ["All", "Product Reference", "Compliance & Standards", "Materials", "Engineering Data", "Process & SOP"];
  const filteredDocs = useMemo(() => {
    return displayDocs.filter(doc => {
      const matchesCategory = selectedCategory === "All" || doc.category === selectedCategory;
      const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) || doc.docCode.toLowerCase().includes(searchQuery.toLowerCase()) || doc.summary.toLowerCase().includes(searchQuery.toLowerCase()) || doc.specsHighlight.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [displayDocs, selectedCategory, searchQuery]);
  return <div className="space-y-8">
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 p-4 rounded-lg bg-card border border-border">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
          <Input type="search" placeholder="Search by doc code, standard (ASTM/ISO), alloy, or keyword..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-full" />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full md:w-auto">
            <TabsList className="bg-muted/70 p-1">
              {categories.map((cat, i) => <TabsTrigger key={cat} value={cat} data-index={i} className="text-sm px-3 py-1">
                  {cat}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground border-b border-border pb-3 px-1">
        <div className="flex items-center gap-2">
          <BookOpen className="size-4 text-primary" aria-hidden="true" />
          <span>Showing <strong>{filteredDocs.length}</strong> verified technical references</span>
        </div>
        <div className="hidden sm:flex items-center gap-4">
          <span className="flex items-center gap-1.5"><ShieldCheck className="size-3.5 text-primary" aria-hidden="true" /> ISO 9044 Verified</span>
          <span className="flex items-center gap-1.5"><FileText className="size-3.5 text-primary" aria-hidden="true" /> ASTM E2016 Compliant</span>
        </div>
      </div>

      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((_, i) => <div key={i} data-index={i} className="h-64 rounded-lg bg-muted/40 border border-border" />)}
        </div> : filteredDocs.length === 0 ? <div className="text-center py-12 px-4 rounded-lg border border-dashed border-border bg-card">
          <CircleAlert className="size-8 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <h3 className="text-base font-semibold text-foreground mb-1">No technical documents match your search</h3>
          <p className="text-sm text-muted-foreground mb-4">Try adjusting your search query or choosing a different category tab.</p>
          <Button variant="outline" size="sm" onClick={() => {
        setSearchQuery("");
        setSelectedCategory("All");
      }}>
            Reset Filters
          </Button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocs.map((doc, i) => <Card key={doc.id} data-index={i} className="relative flex flex-col justify-between border-border hover:border-primary/50 transition-colors duration-200 bg-card mf-hover-lift">
              <CardHeader className="space-y-3 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="font-mono mf-caption tracking-wide bg-muted text-foreground border-border">
                    {doc.docCode}
                  </Badge>
                  <Badge variant="outline" className="mf-caption bg-card text-muted-foreground border-border">
                    {doc.category}
                  </Badge>
                </div>
                <CardTitle className="text-base font-semibold leading-snug hover:text-primary transition-colors line-clamp-2">
                  <Link to={doc.link} className="hover:text-primary transition-colors">
                    {doc.title}
                  </Link>
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3 text-sm text-muted-foreground flex-1">
                <p className="line-clamp-3 leading-relaxed text-sm">
                  {doc.summary}
                </p>

                {doc.specsHighlight && <div className="p-2.5 rounded-md bg-muted border border-border text-sm font-mono text-foreground flex items-center gap-2">
                    <Layers className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span className="truncate">{doc.specsHighlight}</span>
                  </div>}
              </CardContent>

              <CardFooter className="pt-3 border-t border-border/60 flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <FileText className="size-3.5 text-muted-foreground" aria-hidden="true" />
                  <span>{doc.formatLabel}</span>
                  <span>•</span>
                  <span>{doc.pageCount}</span>
                </div>

                <Link to={doc.link} className="inline-flex items-center gap-1 text-primary hover:underline font-medium text-sm">
                  <span>View Specs</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </CardFooter>
            </Card>)}
        </div>}
    </div>;
}
export default function TechnicalReferenceGrid() {
  const {
    lang
  } = useLanguage();
  const wp_query_params = {
    post_type: "technical_document",
    per_page: 12,
    order: "asc",
    orderby: "title"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="technicalreferencegrid" data-nav="light" className="relative mf-section bg-muted/50 text-foreground overflow-hidden border-t border-border">
      <div className="mf-container relative z-10">
        <div className="max-w-3xl mb-12 space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border bg-card mf-caption font-mono font-medium text-foreground">
            <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
            <span>Engineering Resources & Data</span>
          </div>

          <h2 className="mf-h2 text-foreground">
            Technical Reference Library & Standards
          </h2>

          <p className="mf-lead text-muted-foreground">
            Access engineering specifications, ISO/ASTM wire mesh standards, corrosion resistance matrices, and calculation models designed for industrial process engineers and OEM procurement teams.
          </p>
        </div>

        <div className="mt-8 mb-6 p-4 rounded-lg border border-border bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-md bg-muted text-primary border border-border shrink-0">
              <BookOpen className="size-5" />
            </div>
            <div>
              <div className="font-semibold text-sm text-foreground">
                {pick(lang, {
                en: "Filtration Fundamentals",
                zh: "过滤基础与机理指南",
                es: "Fundamentos de filtración",
                de: "Filtrationsgrundlagen",
                fr: "Fondamentaux de la filtration",
                pt: "Fundamentos de filtração"
              })}
              </div>
              <div className="text-xs text-muted-foreground">
                {pick(lang, {
                en: "Particle retention mechanics, weave structures, and surface vs depth media principles.",
                zh: "颗粒拦截机理、编织结构及表面与深层过滤介质工程解析。",
                es: "Mecanismos de retención de partículas, estructuras tejidas y principios de medios.",
                de: "Partikelrückhaltemechanismen, Gewebestrukturen und Filtermedienprinzipien.",
                fr: "Mécanismes de rétention des particules, structures tissées et principes des médias.",
                pt: "Mecanismos de retenção de partículas, estruturas de trama e princípios de meios filtrantes."
              })}
              </div>
            </div>
          </div>
          <Link to="/FiltrationFundamentalsPage" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline shrink-0">
            <span>
              {pick(lang, {
              en: "Explore Fundamentals",
              zh: "探索过滤基础",
              es: "Explorar fundamentos",
              de: "Grundlagen entdecken",
              fr: "Découvrir les fondamentaux",
              pt: "Explorar fundamentos"
            })}
            </span>
            <ArrowRight className="size-4" />
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "technical_document",
        per_page: 12,
        order: "asc",
        orderby: "title",
        status: ["publish"]
      }}>
          <TechnicalReferenceConsumer />
        </PostsProvider>

        <div className="mt-12">
          <InlineCta text="Need custom wire weave calculations, alloy verification sheets, or bespoke mesh specifications?" buttonLabel="Request Custom Spec Sheet" to="/RequestAQuotePage" />
        </div>
      </div>
    </section>;
}